import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  apiPost,
  companyIdSelector,
  errors,
  getDriverBulkSendMessagePath,
  IApiError,
  IPassengersSendMessage,
  Log,
  normalizeErrors,
  SimpleResult,
  toForbiddenTranslatableItem,
  translateInfoItem,
} from "@ryde/shared";

export const useDriversBulkActions = () => {
  const setApiFetching = useSetRecoilState(apiDataFetchingAtom);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { t } = useTranslation();

  const bulkSendMessageToDrivers = useEvent(
    async (payload: Partial<IPassengersSendMessage>): Promise<boolean> => {
      try {
        setErrors(undefined);
        setApiFetching(true);
        const requestUrl = getDriverBulkSendMessagePath(companyId || 0);
        const response = await apiPost<SimpleResult<unknown> | undefined>(
          requestUrl,
          {
            body: payload,
          },
        );

        if (!response?.success) {
          Log.errorAndToast(t("errors.failedToSendMessagesToDrivers"));
          return false;
        }

        Log.infoAndToast(t("driver.bulk.messagesSentToDrivers"));
        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToSendMessagesToDrivers"),
            errorDetails,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(t("errors.failedToSendMessagesToDrivers"), error);
        setErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setApiFetching(false);
      }
    },
  );

  return {
    bulkSendMessageToDrivers,
  };
};
